import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const returnTo = params.get('returnTo') || '/home';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (typeof login !== 'function') {
      toast({
        title: 'Login unavailable',
        description: 'Firebase is not configured.',
      });
      return;
    }

    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail || !password) {
      toast({
        title: 'Missing details',
        description: 'Please enter your email and password.',
      });
      return;
    }

    setSubmitting(true);
    try {
      await login(normalizedEmail, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      toast({
        title: 'Login failed',
        description: String(err?.message || 'Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
        <h1 className="text-2xl font-medium tracking-wide">SIGN IN</h1>
        <p className="mt-2 text-sm text-gray-600">
          Sign in to checkout and track your orders.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div>
            <label className="block text-xs tracking-widest font-medium text-gray-600">EMAIL</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-xs tracking-widest font-medium text-gray-600">PASSWORD</label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black"
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-black text-white py-4 text-sm font-medium tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-60"
          >
            {submitting ? 'SIGNING IN...' : 'SIGN IN'}
          </button>
        </form>

        <div className="mt-6 text-sm text-gray-600">
          Don&apos;t have an account?{' '}
          <Link
            to={`/signup?returnTo=${encodeURIComponent(returnTo)}`}
            className="text-black underline hover:opacity-70 transition-opacity"
          >
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
